'use client';

import { useCallback, useEffect } from 'react';
import {
  Box,
  Divider,
  MenuItem,
  MenuList,
  Paper,
  Portal,
  Switch,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';

import { useClickOutside, useT, useUserSettingsMenu } from '@/hooks';
import { UserMenuItem } from './subcomponents/UserMenuItem';
import { UserMenuToggle } from './subcomponents/UserMenuToggle';
import { UserProfileHeader } from './subcomponents/UserProfileHeader';

import { FOOTER_MENU_LINKS, PRIMARY_MENU_ITEMS } from '@/constants';

export const UserSettingsMenu = () => {
  const { t } = useT();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const {
    anchorRef,
    isMenuVisible,
    menuPosition,
    handleMenuToggle,
    closeMenu,
    themeMode,
    toggleTheme,
    isAuthenticated,
    user,
    handleLogout,
    handleLinkClick,
    isLoggingOut,
  } = useUserSettingsMenu();

  const menuContainerRef = useClickOutside<HTMLDivElement>(closeMenu, isMenuVisible);

  useEffect(() => {
    if (!isMenuVisible) return;
    const handleResize = () => handleMenuToggle();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuVisible, handleMenuToggle]);

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={anchorRef}>
        <UserMenuToggle
          isAuthenticated={isAuthenticated}
          user={user}
          onClick={handleMenuToggle}
          isOpen={isMenuVisible}
        />
      </Box>

      {isMenuVisible && menuPosition && (
        <Portal>
          <Box
            onClick={closeMenu}
            sx={{ position: 'fixed', inset: 0, zIndex: 11999, bgcolor: 'transparent' }}
          />
          <Paper
            ref={menuContainerRef}
            elevation={8}
            onClick={handleStopPropagation}
            sx={{
              position: 'fixed',
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
              minWidth: 280,
              maxWidth: isMobile ? 'calc(100vw - 32px)' : 'none',
              zIndex: 12000,
              animation: 'slideIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '@keyframes slideIn': {
                from: { opacity: 0, transform: 'translateY(-12px) scale(0.98)' },
                to: { opacity: 1, transform: 'translateY(0) scale(1)' },
              },
              borderRadius: '16px',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(30, 31, 35, 0.85)'
                  : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(16px)',
              border: '1px solid',
              borderColor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
              boxShadow: (theme) =>
                theme.palette.mode === 'dark'
                  ? '0 12px 48px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.05)'
                  : '0 12px 48px -12px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.02)',
            }}
          >
            <MenuList sx={{ py: 1 }}>
              {isAuthenticated && (
                <UserProfileHeader user={user} onClose={closeMenu} onNavigate={handleLinkClick} />
              )}

              {PRIMARY_MENU_ITEMS.map((link) => (
                <UserMenuItem key={link.label} item={link} onClick={handleLinkClick} />
              ))}

              <MenuItem
                sx={{
                  py: 0.85,
                  px: 1.75,
                  borderRadius: '10px',
                  mx: 1,
                  mb: 0.5,
                  justifyContent: 'space-between',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.04)',
                  },
                }}
                onClick={toggleTheme}
              >
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  {t('menu.darkMode')}
                </Typography>
                <Switch
                  size="small"
                  checked={themeMode === 'dark'}
                  onChange={toggleTheme}
                  onClick={handleStopPropagation}
                />
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              {isAuthenticated && (
                <UserMenuItem
                  item={{ label: isLoggingOut ? t('menu.loggingOut') : t('menu.logOut') }}
                  color={isLoggingOut ? 'text.disabled' : 'error.main'}
                  onClick={isLoggingOut ? undefined : handleLogout}
                  sx={isLoggingOut ? { pointerEvents: 'none', opacity: 0.7 } : {}}
                />
              )}

              {FOOTER_MENU_LINKS.map((link) => (
                <UserMenuItem key={link.label} item={link} isSecondary onClick={handleLinkClick} />
              ))}
            </MenuList>
          </Paper>
        </Portal>
      )}
    </Box>
  );
};

export default UserSettingsMenu;
